import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    Box,
    Button,
    Card,
    Container,
    FileInput,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Stack,
    TagsInput,
    Text,
    TextInput,
} from '@mantine/core';
import { MultiSelectCreatable, SearchableSelect } from '../../../common';
import {
    getDatasets,
    getMessageGroups,
    getUserDatasets,
    getUsers,
} from '../../../../http/apiClient';
import { useCallback, useMemo, useState } from 'react';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';

import CSVReader from 'react-csv-reader';
import CreateDatasetModal from '../modals/CreateDatasetModal';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { IdColumn } from './columns';
import { MantineReactTable } from 'mantine-react-table';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import useImportDataRecords from '../../../../http/mutations/data/useImportDataRecords';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const parseOptions = {
    header: false,
    worker: false,
    dynamicTyping: false,
    skipEmptyLines: true,
    preview: 10,
};

const ImportRecordsView = ({ ...props }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const [lines, setLines] = useState([]);
    const [file, setFile] = useState(null);
    const [dataSet, setDataSet] = useState(null);

    const [
        createModalOpen,
        { open: openCreateModal, close: closeCreateModal },
    ] = useDisclosure();

    const userQueryHttpFn = (queryHttpFn, queryParams) => {
        const userId = form.values.userId;
        const searchUserRoutes = hasRole(userProfile, 'SUPER_ADMIN') && userId;

        if (searchUserRoutes) {
            return queryHttpFn({
                userId,
                pagination: queryParams,
            });
        }
        return queryHttpFn({
            pagination: queryParams,
        });
    };

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            dataSetId: '',
            columnMap: {},
            trackingId: '',
            tags: [],
        },
        transformValues: (values) => {
            /**
             * Inverts an object's keys and values.
             * If multiple original keys share the same value,
             * the inverted object's value becomes an array of those keys.
             *
             * @param {Record<string, any>} obj - e.g. { a: 'x', b: 'y', c: 'x' }
             * @returns {Record<string, string | string[]>} - e.g. { x: ['a', 'c'], y: 'b' }
             */
            function invert(obj = {}) {
                return Object.entries(obj).reduce((acc, [key, value]) => {
                    // Coerce value to a string key (objects, numbers, etc. → "value")
                    const valKey = String(value);

                    if (acc[valKey] === undefined) {
                        // first time we see this value
                        acc[valKey] = key;
                    } else if (Array.isArray(acc[valKey])) {
                        // already collecting duplicates
                        acc[valKey].push(key);
                    } else {
                        // second occurrence: convert existing single key to array
                        acc[valKey] = [acc[valKey], key];
                    }
                    return acc;
                }, {});
            }
            return {
                ...values,
                columnMap: invert(values.columnMap),
            };
        },
        validate: {
            userId: (value) => {
                const { message } = genericValidator('User ID', value, {
                    required: hasRole(userProfile, 'SUPER_ADMIN'),
                });
                return message;
            },
            dataSetId: (value) => {
                const { message } = genericValidator('Data set', value, {
                    required: true,
                });
                return message;
            },
            trackingId: (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            tags: (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    const { isPending, mutate: uploadFile } = useImportDataRecords();

    const handleNewDataSet = (dataset) => {
        setDataSet({
            value: dataset.id.toString(),
            label: dataset.name,
        });
        form.setFieldValue('dataSetId', dataset.id.toString());
    };

    const handleFileLoad = (data, fileInfo, originalFile) => {
        const asObj = data.map((array) =>
            Object.assign(
                {},
                array.reduce((obj, value, index) => {
                    obj[index.toString()] = value;
                    return obj;
                }, {})
            )
        );
        setLines(asObj);
        setFile(originalFile);
        form.setFieldValue('file', originalFile);
        console.log(asObj);
    };

    const handleError = () => {
        setLines([]);
    };

    const callback = useCallback(() => {}, [form]);

    const onSubmit = (values) => {
        uploadFile(values, {
            onSuccess: () => {
                setFile(null);
                setLines([]);
                setDataSet(null);
                form.reset();
                form.setFieldValue('file', null);
            },
        });
    };

    const isLoading = false;
    const columns = useMemo(
        () =>
            lines.length > 0
                ? Object.keys(lines[0]).map((x) =>
                      IdColumn(x, form, form.values)
                  )
                : [],
        [lines, form]
    );

    const tableData = useMemo(() => (isLoading ? [] : lines), [lines, form]);

    const tableProps = {
        columns: columns,
        data: tableData,
        enableTableHead: false,
        enableRowActions: false,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: false,
        enableHiding: false,
        enableGlobalFilter: false,
        enableColumnVisibility: false,
        enableFilters: false,
        enableStickyHeader: false,
        enableDensityToggle: false,
        layoutMode: 'semantic',
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        //renderRowActions: ActionsColumn,
        rowCount: lines.length,

        initialState: {},
        state: {
            isLoading,
            showProgressBars: isLoading,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Stack>
                    <Card
                        shadow='sm'
                        padding='lg'
                        radius='md'
                        withBorder>
                        <Card.Section p={'lg'}>
                            <Stack>
                                <Text
                                    h={4}
                                    mb={'sm'}>
                                    Import Data Records
                                </Text>
                                {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                    <SearchableSelect
                                        label={'User'}
                                        withAsterisk
                                        placeholder='User'
                                        queryHttpFn={getUsers}
                                        queryKey={'users'}
                                        optionsMapFn={(user) => ({
                                            label: user.username,
                                            value: user.id.toString(),
                                        })}
                                        defaultValue={{
                                            label: userProfile.username,
                                            value: userProfile.id,
                                        }}
                                        rightSection={
                                            <Button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openCreateModal();
                                                }}>
                                                Create Data Set
                                            </Button>
                                        }
                                        rightSectionWidth={120}
                                        rightSectionPointerEvents={'auto'}
                                        {...form.getInputProps('userId')}
                                    />
                                ) : null}

                                <SearchableSelect
                                    label={'Dataset'}
                                    withAsterisk
                                    placeholder='Select dataset'
                                    queryHttpFn={(queryParams) =>
                                        userQueryHttpFn(
                                            getUserDatasets,
                                            queryParams
                                        )
                                    }
                                    queryKey={
                                        form.values.userId
                                            ? ['data_sets', form.values.userId]
                                            : ['data_sets']
                                    }
                                    optionsMapFn={(dataset) => ({
                                        label: dataset.name,
                                        value: dataset.id.toString(),
                                    })}
                                    defaultValue={dataSet}
                                    {...form.getInputProps('dataSetId')}
                                />

                                <CSVReader
                                    cssClass='mantine-Input-wrapper mantine-FileInput-wrapper'
                                    cssInputClass='mantine-Input-input mantine-FileInput-input'
                                    accept={'.csv, .txt, text/csv, text/plain'}
                                    onFileLoaded={handleFileLoad}
                                    onError={handleError}
                                    parserOptions={parseOptions}
                                />

                                <TextInput
                                    label={'Tracking ID'}
                                    placeholder='Enter a tracking id'
                                    {...form.getInputProps('trackingId')}
                                />

                                <TagsInput
                                    label={'Tags'}
                                    placeholder={'Tags'}
                                    {...form.getInputProps('tags')}
                                />
                            </Stack>
                        </Card.Section>
                    </Card>
                    <MantineReactTable {...tableProps} />
                    <Group justify='flex-end'>
                        <Button
                            size={'xs'}
                            type={'submit'}
                            disabled={!form.isValid() || isPending}>
                            Upload Records
                        </Button>
                    </Group>
                </Stack>
            </form>
            <CreateDatasetModal
                opened={createModalOpen}
                close={closeCreateModal}
                onSuccess={handleNewDataSet}
            />
        </Container>
    );
};
export default ImportRecordsView;
